import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

import cacheKeys from '../../common/cacheKeys';
import { AuthState } from './types';
import { getAuthenticationApiClient } from '../../common/apiClients';
import { getMessageFromError } from '../../common/utils/apiUtils';
import {
  Applicant,
  AuthApplicantCreateRequest,
  AuthLoginCreateRequest,
  AuthRefreshCreateRequest,
  TokenObtainPair,
  TokenObtainPairRequest,
  TokenRefresh
} from '../../generated/api';

const sliceName = 'auth';

export const AuthenticateAdminAsync = createAsyncThunk<TokenObtainPair, TokenObtainPairRequest>(
  `${sliceName}/adminLogin`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAuthenticationApiClient();
      const request: AuthLoginCreateRequest = { tokenObtainPairRequest: arg };
      return await apiClient.authLoginCreate(request);
    } catch (error) {
      return rejectWithValue(await getMessageFromError(error));
    }
  }
);

export const RefreshAdminTokenAsync = createAsyncThunk<TokenRefresh, string>(
  `${sliceName}/adminRefreshToken`,
  async (arg) => {
    const apiClient = await getAuthenticationApiClient();
    const request: AuthRefreshCreateRequest = { tokenRefreshRequest: { refresh: arg } };
    return apiClient.authRefreshCreate(request);
  }
);

export const AuthenticateApplicantAsync = createAsyncThunk<Applicant, string>(
  `${sliceName}/applicantLogin`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getAuthenticationApiClient();
      const request: AuthApplicantCreateRequest = { applicantLoginPayloadRequest: { code: arg } };
      return await apiClient.authApplicantCreate(request);
    } catch (error) {
      return rejectWithValue(await getMessageFromError(error));
    }
  }
);

const initialState: AuthState = {
  applicant: null,
  admin: {
    token: null,
    isLoggedIn: false
  },
  loading: false,
  attemptingLogin: false
};

const authSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    adminSignOut: (state: AuthState) => {
      sessionStorage.removeItem(cacheKeys.adminRefreshTokenKey);
      return { ...state, admin: { token: null, isLoggedIn: false } };
    },
    applicantSignOut: (state: AuthState) => {
      sessionStorage.removeItem(cacheKeys.applicantCode);
      return { ...state, applicant: null };
    },
    setAttemptingLogin: (state: AuthState, action: PayloadAction<boolean>) => {
      return { ...state, attemptingLogin: action.payload };
    }
  },
  extraReducers: {
    [AuthenticateAdminAsync.fulfilled.type]: (state: AuthState, action: PayloadAction<TokenObtainPair>): AuthState => {
      sessionStorage.setItem(cacheKeys.adminRefreshTokenKey, action.payload.refresh);
      return {
        ...state,
        admin: { ...state.admin, token: action.payload, isLoggedIn: true },
        loading: false,
        attemptingLogin: false
      };
    },
    [AuthenticateAdminAsync.pending.type]: (state: AuthState): AuthState => {
      return { ...state, loading: true };
    },
    [AuthenticateAdminAsync.rejected.type]: (state: AuthState): AuthState => {
      return { ...state, admin: { token: null, isLoggedIn: false }, loading: false, attemptingLogin: false };
    },
    [RefreshAdminTokenAsync.fulfilled.type]: (state: AuthState, action: PayloadAction<TokenRefresh>): AuthState => {
      sessionStorage.setItem(cacheKeys.adminRefreshTokenKey, action.payload.refresh);
      return { ...state, admin: { token: action.payload, isLoggedIn: true }, loading: false };
    },
    [RefreshAdminTokenAsync.pending.type]: (state: AuthState): AuthState => {
      return { ...state, loading: true };
    },
    [RefreshAdminTokenAsync.rejected.type]: (state: AuthState): AuthState => {
      sessionStorage.removeItem(cacheKeys.adminRefreshTokenKey);
      return { ...state, admin: { token: null, isLoggedIn: false }, loading: false };
    },
    [AuthenticateApplicantAsync.fulfilled.type]: (state: AuthState, action: PayloadAction<Applicant>): AuthState => {
      sessionStorage.setItem(cacheKeys.applicantCode, action.payload.code);
      return { ...state, applicant: action.payload, loading: false, attemptingLogin: false };
    },
    [AuthenticateApplicantAsync.pending.type]: (state: AuthState): AuthState => {
      return { ...state, loading: true };
    },
    [AuthenticateApplicantAsync.rejected.type]: (state: AuthState): AuthState => {
      sessionStorage.removeItem(cacheKeys.applicantCode);
      return { ...state, applicant: null, loading: false };
    }
  }
});

export const selectAuth = (state: RootState) => state.auth;
export const { adminSignOut, applicantSignOut, setAttemptingLogin } = authSlice.actions;

export default authSlice.reducer;
